import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { AppShell, Group, Title, Button, Stack, Text, Modal } from '@mantine/core'
import { IconMapPin } from '@tabler/icons-react'
import { useUser } from '@clerk/clerk-react'
import { GoogleMapWrapper } from '../ui'
import type { GoogleMapWrapperRef } from '../ui'
import { SpottingForm } from './SpottingForm'
import { SpottingsList } from './SpottingsList'
import { useGeolocation } from '../../hooks'
import { usePWA } from '../../hooks/usePWA'
import { useCreateSpotting, useSpottingsInBounds, useSightingCategories } from '../../hooks/useSpottings'
import type { Spotting } from '../../types/api'
import { SpottingDetail } from './SpottingDetail'
import { SlideUpDrawer, type SlideUpDrawerRef } from './SlideUpDrawer'
import { ProtectedAction } from '../auth'
import { useNavigate } from 'react-router-dom'

export function HomeLayout() {
    const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null)
    const [editingSpotting, setEditingSpotting] = useState<Spotting | null>(null)
    const [viewingSpotting, setViewingSpotting] = useState<Spotting | null>(null)
    const [showWelcomeModal, setShowWelcomeModal] = useState(false)
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([])
    const [mapBounds, setMapBounds] = useState<{
        north: number
        south: number
        east: number
        west: number
    } | null>(null)
    const [isDrawerDragging, setIsDrawerDragging] = useState(false)
    const [drawerMode, setDrawerMode] = useState<'mini' | 'normal' | 'expanded'>('mini') // Start in mini mode
    const [listScrollPosition, setListScrollPosition] = useState(0)
    const [showAuthForMapClick, setShowAuthForMapClick] = useState(false)
    const { location, loading: locationLoading, permission, requestLocation, error } = useGeolocation()
    const { shouldShowInstallPrompt } = usePWA()
    const { isSignedIn } = useUser()
    const mapRef = useRef<GoogleMapWrapperRef>(null)
    const drawerRef = useRef<SlideUpDrawerRef>(null)
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
    const navigate = useNavigate()

    // React Query hooks - Use bounds filtering for both list and map
    const createSpottingMutation = useCreateSpotting()
    const { data: categories } = useSightingCategories()

    // Use stable options object to prevent infinite renders
    const queryOptions = useMemo(() => ({
        page: 1,
        limit: 100,
        enabled: !!mapBounds,
        categoryIds: selectedCategoryIds, // Always pass the array, even if empty
    }), [mapBounds, selectedCategoryIds])

    const { data: spottingsResponse } = useSpottingsInBounds(mapBounds, queryOptions)

    // Initialize selectedCategoryIds with all categories when they load (only once)
    const [hasInitializedCategories, setHasInitializedCategories] = useState(false)
    useEffect(() => {
        if (categories && categories.length > 0 && !hasInitializedCategories) {
            setSelectedCategoryIds(categories.map(cat => cat.id))
            setHasInitializedCategories(true)
        }
    }, [categories, hasInitializedCategories])

    // Check if this is the user's first visit
    useEffect(() => {
        const hasVisited = localStorage.getItem('spotter-has-visited')
        if (!hasVisited) {
            setShowWelcomeModal(true)
            localStorage.setItem('spotter-has-visited', 'true')
        }
    }, [])

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768)
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        // Don't handle map clicks when drawer is being dragged
        if (isDrawerDragging) return

        // Don't handle map clicks when viewing a spotting detail
        if (viewingSpotting) return

        const lat = event.latLng?.lat()
        const lng = event.latLng?.lng()

        if (lat !== undefined && lng !== undefined) {
            // Check if user is signed in before allowing spotting creation
            if (!isSignedIn) {
                setShowAuthForMapClick(true)
                return
            }

            setSelectedLocation({ lat, lng })
            setEditingSpotting(null) // Clear any editing state
        }
    }, [isDrawerDragging, viewingSpotting, isSignedIn])

    const handleBoundsChanged = useCallback((bounds: {
        north: number
        south: number
        east: number
        west: number
    }) => {
        setMapBounds(bounds)
    }, [])

    const handleCreateAtMyLocation = useCallback(() => {
        if (!isSignedIn) {

            return
        }

        if (location) {
            setSelectedLocation(location)
            setEditingSpotting(null) // Clear any editing state
            // Center the map on user location
            mapRef.current?.recenterToUser()
        } else {
            // Request location if we don't have it yet (important for Safari)
            requestLocation()
        }
    }, [location, requestLocation])

    const handleSpottingSubmit = useCallback(async (formData: FormData) => {
        try {
            await createSpottingMutation.mutateAsync(formData);

            // Reset form state
            setSelectedLocation(null);
            setEditingSpotting(null);
        } catch (error) {
            console.error('Failed to save spotting:', error);
            // You could show a toast notification here
        }
    }, [createSpottingMutation]);

    const handleFormCancel = useCallback(() => {
        setSelectedLocation(null)
        setEditingSpotting(null)
        setViewingSpotting(null)
    }, [])

    const handleSpottingClick = useCallback((spotting: Spotting, scrollPosition?: number) => {
        // Save scroll position if provided
        if (scrollPosition !== undefined) {
            setListScrollPosition(scrollPosition)
        }
        // Show spotting details
        setViewingSpotting(spotting)
        // Clear any editing or location selection
        setEditingSpotting(null)
        setSelectedLocation(null)
    }, [])

    const handleBackToList = useCallback(() => {
        setViewingSpotting(null)
        // Scroll position will be restored by the SpottingsList component
    }, [])

    const handleMarkerClick = useCallback((spottingId: string) => {
        // Don't handle marker clicks when drawer is being dragged
        if (isDrawerDragging) return

        // Find the spotting by ID from the current data
        const spotting = spottingsResponse?.data?.find(s => s.id === spottingId)
        if (spotting) {
            handleSpottingClick(spotting)

            // If on mobile and drawer is in mini state, expand it to show the spotting details
            if (isMobile && drawerMode === 'mini') {
                drawerRef.current?.expandToNormal()
            }
        }
    }, [spottingsResponse?.data, handleSpottingClick, isDrawerDragging, isMobile, drawerMode])

    const handleEditSpotting = useCallback((spotting: Spotting) => {
        setEditingSpotting(spotting)
        setSelectedLocation({ lat: spotting.latitude, lng: spotting.longitude })
    }, [])

    // Get spottings for map display - now uses the same filtered data as the list
    const spottingsForMap = useMemo(() => {
        return spottingsResponse?.data?.map(spotting => ({
            id: spotting.id,
            name: spotting.name,
            location: { lat: spotting.latitude, lng: spotting.longitude },
            imageUrl: spotting.imageUrl
        })) || []
    }, [spottingsResponse?.data])

    // Handle drawer mode changes
    const handleDrawerModeChange = useCallback((mode: 'mini' | 'normal' | 'expanded') => {
        setDrawerMode(mode)
    }, [])

    return (
        <>
            {/* <AppHeader /> */}

            {/* Main area - Map View with Mobile Drawer */}
            <AppShell.Main className={`flex ${isMobile ? 'map-container-with-drawer' : ''} ${isDrawerDragging ? 'drawer-dragging' : ''} ${isMobile ? `drawer-${drawerMode}` : ''} ${viewingSpotting ? 'map-interaction-disabled' : ''}`}>
                <GoogleMapWrapper
                    ref={mapRef}
                    onMapClick={handleMapClick}
                    onBoundsChanged={handleBoundsChanged}
                    temporaryPin={selectedLocation}
                    spottings={spottingsForMap}
                    selectedSpottingId={viewingSpotting?.id || null}
                    userLocation={location}
                    locationLoading={locationLoading}
                    locationError={error}
                    locationPermission={permission}
                    onRequestLocation={requestLocation}
                    onMarkerClick={handleMarkerClick}
                    shouldShowInstallPrompt={shouldShowInstallPrompt}
                    onInstallPromptClick={() => navigate('/help/add-to-home-screen')}
                />
            </AppShell.Main>

            {/* Mobile slide-up drawer */}
            {isMobile && (
                <SlideUpDrawer
                    ref={drawerRef}
                    onDragStateChange={setIsDrawerDragging}
                    onModeChange={handleDrawerModeChange}
                >
                    {viewingSpotting ? (
                        <SpottingDetail
                            spotting={viewingSpotting}
                            onBack={handleBackToList}
                        />
                    ) : selectedLocation || editingSpotting ? (
                        <SpottingForm
                            location={selectedLocation || (editingSpotting ? { lat: editingSpotting.latitude, lng: editingSpotting.longitude } : { lat: 0, lng: 0 })}
                            onSubmit={handleSpottingSubmit}
                            onCancel={handleFormCancel}
                            isLoading={createSpottingMutation.isPending}
                            initialData={editingSpotting ? {
                                description: editingSpotting.description || '',
                                categoryId: editingSpotting.categoryId,
                            } : undefined}
                            isEditing={!!editingSpotting}
                        />
                    ) : (
                        <Stack gap="md" className="h-full">
                            {/* Create at my location button - show when permission is granted or prompt (Safari) */}
                            {(permission === 'granted' || permission === 'prompt' || permission === null) && (
                                <div className="flex flex-col gap-2">
                                    {isSignedIn ? <Button
                                        leftSection={<IconMapPin size={16} />}
                                        onClick={handleCreateAtMyLocation}
                                        variant="filled"
                                        size="md"
                                        fullWidth
                                        loading={locationLoading}
                                        disabled={false}
                                        style={{ touchAction: 'manipulation' }}
                                    >
                                        {location ? 'Create Spotting at my Location' : 'Use My Location'}
                                    </Button> : <ProtectedAction
                                        fallbackText="Sign In"
                                        fallbackIcon={undefined}
                                        fallbackVariant="filled"
                                        fallbackSize="sm"
                                        fallbackFullWidth={false}
                                        fallbackColor="secondary"
                                    >
                                        <div />
                                    </ProtectedAction>}
                                    <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
                                        - OR - {isSignedIn ? 'click on the map' : (location ? 'sign in and click on the map' : 'click on the map')}
                                        {shouldShowInstallPrompt && (
                                            <div className="text-center text-xs text-orange-600 dark:text-orange-400 mt-1">
                                                💡 Add us to your home screen for the best experience!
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Spottings list with React Query and bounds filtering */}
                            <SpottingsList
                                onSpottingClick={handleSpottingClick}
                                onEditSpotting={handleEditSpotting}
                                mapBounds={mapBounds}
                                savedScrollPosition={listScrollPosition}
                                onScrollPositionChange={setListScrollPosition}
                                selectedCategoryIds={selectedCategoryIds}
                                onCategoryChange={setSelectedCategoryIds}
                                isMobile={isMobile}
                            />
                        </Stack>
                    )}
                </SlideUpDrawer>
            )}

            {/* Desktop spottings list overlay */}
            {!isMobile && !selectedLocation && !editingSpotting && !viewingSpotting && (
                <div
                    style={{
                        position: 'fixed',
                        top: 70,
                        left: 0,
                        bottom: 0,
                        width: '400px',
                        backgroundColor: 'var(--mantine-color-body)',
                        border: '1px solid var(--mantine-color-default-border)',
                        // borderRadius: '8px',
                        zIndex: 200,
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <div style={{ height: '100%', padding: '20px', overflowY: 'auto' }}>
                        <Stack gap="md" className="h-full">
                            {/* Create at my location button - show when permission is granted or prompt (Safari) */}
                            {(permission === 'granted' || permission === 'prompt' || permission === null) && (
                                <div>
                                    <Button
                                        leftSection={<IconMapPin size={16} />}
                                        onClick={handleCreateAtMyLocation}
                                        variant="filled"
                                        size="md"
                                        fullWidth
                                        loading={locationLoading}
                                        disabled={false}
                                        style={{ touchAction: 'manipulation' }}
                                    >
                                        {location ? (isSignedIn ? 'Create Spotting at my Location' : 'Use My Location') : 'Use My Location'}
                                    </Button>
                                    <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
                                        - OR - {isSignedIn ? 'click on the map' : (location ? 'sign in and click on the map' : 'click on the map')}
                                        {shouldShowInstallPrompt && (
                                            <div className="text-center text-xs text-orange-600 dark:text-orange-400 mt-1">
                                                💡 Add us to your home screen for the best experience!
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Spottings list with React Query and bounds filtering */}
                            <SpottingsList
                                onSpottingClick={handleSpottingClick}
                                onEditSpotting={handleEditSpotting}
                                mapBounds={mapBounds}
                                savedScrollPosition={listScrollPosition}
                                onScrollPositionChange={setListScrollPosition}
                                selectedCategoryIds={selectedCategoryIds}
                                onCategoryChange={setSelectedCategoryIds}
                                isMobile={isMobile}
                            />
                        </Stack>
                    </div>
                </div>
            )}

            {/* Desktop sidebar overlay for forms and details */}
            {!isMobile && (selectedLocation || editingSpotting || viewingSpotting) && (
                <div
                    style={{
                        position: 'fixed',
                        top: '70px',
                        left: '20px',
                        bottom: '20px',
                        width: '400px',
                        backgroundColor: 'var(--mantine-color-body)',
                        border: '1px solid var(--mantine-color-default-border)',
                        borderRadius: '8px',
                        zIndex: 1000,
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <div style={{ height: '100%', padding: '20px', overflowY: 'auto' }}>
                        {viewingSpotting ? (
                            <SpottingDetail
                                spotting={viewingSpotting}
                                onBack={handleBackToList}
                            />
                        ) : (
                            <SpottingForm
                                location={selectedLocation || (editingSpotting ? { lat: editingSpotting.latitude, lng: editingSpotting.longitude } : { lat: 0, lng: 0 })}
                                onSubmit={handleSpottingSubmit}
                                onCancel={handleFormCancel}
                                isLoading={createSpottingMutation.isPending}
                                initialData={editingSpotting ? {
                                    description: editingSpotting.description || '',
                                    categoryId: editingSpotting.categoryId,
                                } : undefined}
                                isEditing={!!editingSpotting}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Welcome Modal - Only shown on first visit */}
            <Modal
                opened={showWelcomeModal}
                onClose={() => setShowWelcomeModal(false)}
                title={<Title order={2} size="h2">Welcome to Spotter</Title>}
                centered
                size="lg"
                closeOnClickOutside={false}
                closeOnEscape={true}
            >
                <Stack gap="lg">
                    <Stack gap="md">
                        <Text>
                            Because you never know what you'll see out there — or who's watching you.
                        </Text>
                        <Text>
                            Spotter keeps you in the loop on the wild, weird, and unexpected around you. From surprise celebrity encounters to free curbside scores, rare cars to ICE cold beverages (if you know, you know), Spotter helps you stay one step ahead.
                        </Text>
                        <Text>
                            Sign up to get real-time text alerts whenever something worth seeing pops up nearby. Don't forget to enable location services to unlock the full Spotter experience.
                        </Text>
                        <Text>
                            Having trouble? Visit our help center: <Text component="span" c="blue" td="underline" style={{ cursor: 'pointer' }} onClick={() => {
                                setShowWelcomeModal(false)
                                navigate('/help')
                            }}>Help & FAQ</Text>
                        </Text>
                    </Stack>

                    <Group justify="flex-end" mt="md">
                        <Button onClick={() => setShowWelcomeModal(false)}>
                            Get Started
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* Auth Modal for Map Clicks */}
            <Modal
                opened={showAuthForMapClick}
                onClose={() => setShowAuthForMapClick(false)}
                title="Sign in to create spottings"
                centered
                size="md"
            >
                <Text size="sm" c="dimmed" mb="lg">
                    You need to sign in to create spottings. You can still browse and view existing spottings without an account.
                </Text>
                <ProtectedAction
                    fallbackText="Sign in to continue"
                    fallbackAction={() => setShowAuthForMapClick(false)}
                >
                    <div />
                </ProtectedAction>
            </Modal>
        </>
    )
} 